const BASE = import.meta.env.VITE_API_BASE_URL ?? '';

/**
 * Two ways to be signed in.
 *
 * A session cookie from Google (Phase 8) is the ordinary path and needs nothing stored
 * here — the browser sends it. A pasted API token stays supported for development and
 * for driving the API by hand; when both exist the token wins, matching the server.
 */
const TOKEN_KEY = 'planner.token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token: string) => localStorage.setItem(TOKEN_KEY, token);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    // Send the session cookie, including when the API is on another origin in dev.
    credentials: 'include',
    headers: {
      // Only claim a JSON body when there is one.
      ...(init.body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      error?: { code: string; message: string };
    } | null;
    throw new ApiError(
      res.status,
      body?.error?.code ?? 'unknown',
      body?.error?.message ?? res.statusText,
    );
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

const send = <T>(method: string, path: string, body?: unknown) =>
  apiFetch<T>(path, {
    method,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

// --- types ----------------------------------------------------------------

export type TaskStatus = 'inbox' | 'next' | 'in_progress' | 'parked' | 'done' | 'dropped';
export type GoalStatus = 'active' | 'paused' | 'done' | 'archived';

export interface Me {
  id: string;
  email: string;
  displayName: string;
  timezone: string;
  /** Null when signed in through the browser: a session is not a token. */
  token: { id: string; name: string; scopes: string[]; expiresAt: string | null } | null;
  authenticatedVia: 'session' | 'token';
  /** The hours the day view draws. Not the hours the planner proposes work in. */
  dayWindow: { startHour: number; endHour: number };
}

export interface Health {
  status: 'ok' | 'degraded';
  database: 'up' | 'down';
  version: string;
  /** False on a deployment with no Google credentials: the button would be dead. */
  googleLogin: boolean;
  /** Whether the sign-in screen should offer to create an account. */
  registrationOpen: boolean;
}

export interface Task {
  id: string;
  title: string;
  descriptionMd: string;
  status: TaskStatus;
  priority: number;
  dueAt: string | null;
  scheduledDate: string | null;
  estimateMinutes: number | null;
  complexityPoints: number | null;
  parentTaskId: string | null;
  projectId: string | null;
  templateId: string | null;
  goalIds: string[];
  isBlocked: boolean;
  /** The open tasks holding this one up, so a surface can name them rather than only flag them. */
  blockedBy: { id: string; title: string }[];
  /**
   * Somebody outside the app has the ball. Derived from `waitingOn`, never stored — the same
   * ruling `isBlocked` gets.
   */
  isWaiting: boolean;
  /**
   * The promised day has gone by, judged **by the server** in the account's zone.
   *
   * Rendered, never recomputed, for the reason `waitingDays` is: `promisedBy` is a calendar day
   * and comparing it to the browser's idea of today answers in the browser's zone. It is also
   * the same rule the `overdue` filter selects by, so a row on the rail and the badge on it agree
   * by construction rather than because two comparisons happened to match.
   */
  isOverdue: boolean;
  /** Who has it. A name, not a user: nothing is ever sent to them. */
  waitingOn: string | null;
  waitingSince: string | null;
  /** The day they promised it for, `YYYY-MM-DD` in the account's zone. */
  promisedBy: string | null;
  lastChasedAt: string | null;
  chaseAfterDays: number | null;
  /**
   * How many times this wait has been chased. Zero on a wait nobody has chased, null when there
   * is no wait at all — and it restarts at zero when a wait is re-set, because the chases before
   * that belonged to a promise that is over.
   */
  chaseCount: number | null;
  /**
   * Whole days of silence, counted **by the server** in the account's zone, from the last chase
   * or from the start of the wait when nobody has been chased.
   *
   * Rendered, never recomputed. Subtracting `waitingSince` from `Date.now()` in the browser
   * answers in the browser's zone and by elapsed hours, so it reads one day short for most of
   * the day and disagrees with the same number in the thread and in an agent's answer — which is
   * the whole reason INV-UTC puts this on the payload.
   */
  waitingDays: number | null;
  /** Applied labels. Written through `setTaskLabels`, never through `updateTask`. */
  labelIds: string[];
  /** Who has it. Null is *unclaimed* — a state a project view is scanned for, not an absence. */
  assigneeUserId: string | null;
  /** How far the subtasks have got. `total` leaves out dropped work — see `SubtaskPill`. */
  subtasks: { done: number; total: number };
  /**
   * How far the checklist has got, and the next unticked step — see `ChecklistLine`.
   *
   * Not the same question as `subtasks`, and the row says which is which: steps inside one task
   * against work items of their own. `next` is null once everything is ticked, and the whole
   * object is zeroes for a task with no checklist.
   */
  checklist: { done: number; total: number; next: string | null };
  /**
   * The task this one is part of. Null at the top level, or when it is out of reach.
   *
   * Carries its colour and deadline because both are inherited *live*: a subtask with no deadline
   * of its own shows its parent's, so moving the parent's date moves every inheriting child and
   * nothing goes stale.
   */
  parent: { id: string; title: string; color: string | null; dueAt: string | null } | null;
  /** Chosen for this task specifically. Null means "ask the containers" — see `colors.ts`. */
  color: string | null;
  sortOrder: number;
  /**
   * That this task repeats, and the repeat in words.
   *
   * Both read-only: a task's rule belongs to the template it came from, and `updateTask` will not
   * patch it. Draw `recurrenceText`, never `recurrenceRule` — the rule is an RRULE and the text is
   * the sentence `describeRule` makes of it, which is what `Templates.tsx` already draws for a
   * template.
   */
  recurrenceRule: string | null;
  recurrenceText: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

export interface Goal {
  id: string;
  parentGoalId: string | null;
  path: string;
  depth: number;
  title: string;
  descriptionMd: string;
  status: GoalStatus;
  /**
   * Whether the goal is still being pursued, judged by the server.
   *
   * `status === 'active'` in practice, but not re-derived here: the shortlist, the week review
   * and the goal's own page all ask the same question, and each browser copy of the rule was one
   * more place to answer it differently — which is how "paused" ended up meaning one thing to
   * "Goals today" and another to the day plan. `status` still carries *which* way it stopped,
   * because that is the word a paused goal's page prints.
   */
  live: boolean;
  targetDate: string | null;
  color: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

interface GoalProgress {
  ratio: number;
  percent: number;
  completedWeight: number;
  /** Not done and not parked — what *Open* means. See `workState` in core. */
  openWeight: number;
  /** Parked: still inside `totalWeight`, never counted as open. */
  parkedWeight: number;
  totalWeight: number;
  directTasksDone: number;
  directTasksOpen: number;
  directTasksParked: number;
  directTasksTotal: number;
}

/* `not_judged`: a goal carried by habits, which pace cannot measure — see `goalPace.ts`. */
type PaceState = 'no_target' | 'done' | 'overdue' | 'behind' | 'on_track' | 'ahead' | 'not_judged';

/** Progress against the clock, rather than progress on its own. Derived, never stored. */
export interface GoalPace {
  state: PaceState;
  elapsedRatio: number | null;
  deltaRatio: number | null;
  daysRemaining: number | null;
  needsNextAction: boolean;
  /**
   * Why there is nothing open. `not_started` and `ready_to_finish` are opposite situations that
   * used to render identically — an amber warning and a single offer to add more work.
   */
  nextActionState: 'none' | 'not_started' | 'ready_to_finish';
}

/** Whether a goal is still moving — the verdict a goal without a target date can be given. */
export interface GoalMomentum {
  state: 'none' | 'moving' | 'slowing' | 'dormant';
  /** Days since the last completion, or since creation if there has never been one. */
  daysSinceMoved: number | null;
  hasEverMoved: boolean;
}

/**
 * A habit serving a goal.
 *
 * Not part of `progress`, deliberately: a habit never completes, so folding it into a
 * percentage would make a goal with all its tasks done read as 60% because one week dipped.
 * Work finished and behaviour maintained are both worth knowing and are not the same quantity.
 */
interface GoalHabit {
  id: string;
  name: string;
  /* The same union everywhere — see `DayHabit.state` for what typing it as `string` cost. */
  state: HabitState;
  onTrack: boolean;
  /*
    Sent by every route that sends a habit — `z.string()`, not optional, in `routes/habits.ts`,
    `day.ts`, `goals.ts` and `review.ts`.

    It was declared optional here, and four surfaces each wrote their own answer to a case the
    server cannot produce: Habits fell back to `stateLabel`, Today to `stateLabel`, the goal page
    to `STATE_LABEL[state]`, and Reflection to no word at all. Four fallbacks for one absent fact
    is how the second vocabulary survived a phase that existed to remove it — "on track" for a
    habit with nothing logged was reachable only through them.
  */
  verdict: HabitVerdict;
  actual: number;
  target: number;
  period: HabitPeriod;
  unit: string | null;
  /** Which side of `target` is the good one — a limit's figure says *allowed*. */
  polarity: HabitPolarity;
  isAbstinence: boolean;
  /** True for a ceiling you read rather than one you tally — said by the service. */
  isReading: boolean;
  /** The latest reading and its day — see `HabitProgress.lastReading`. */
  lastReading: number | null;
  lastReadOn: string | null;
}

export interface GoalTreeNode {
  goal: Goal;
  progress: GoalProgress;
  pace: GoalPace;
  momentum: GoalMomentum;
  habits: GoalHabit[];
  children: GoalTreeNode[];
}

export interface ChecklistItem {
  id: string;
  taskId: string;
  label: string;
  doneAt: string | null;
  sortOrder: number;
  createdAt: string;
}

export interface Comment {
  id: string;
  /**
   * Who wrote it, named through the project's membership. Null on a system entry and on a
   * personal thread; a null `displayName` is somebody who has since left the project — the entry
   * is still theirs, so the thread says "a former member" rather than nothing.
   */
  author: { userId: string; displayName: string | null } | null;
  kind: 'user' | 'agent' | 'system';
  bodyMd: string;
  actorTokenId: string | null;
  createdAt: string;
  editedAt: string | null;
}

export interface TaskLink {
  id: string;
  type: 'blocks' | 'relates';
  direction: 'outgoing' | 'incoming';
  otherTaskId: string;
  otherTaskTitle: string;
  otherTaskStatus: string;
  createdAt: string;
}

export interface TaskFilter {
  /**
   * One status or several.
   *
   * The service and the querystring have both accepted an array since Phase 1; only this client
   * was single-valued, which is why a board asking for three columns at once needed a change
   * here and nowhere else.
   */
  status?: TaskStatus | TaskStatus[];
  goalId?: string;
  /** With `goalId`, include tasks on its descendants. */
  includeSubgoals?: boolean;
  q?: string;
  /**
   * Unsorted capture: no goal, no project, no parent, whatever the status.
   *
   * Named `inbox` here until Phase 76, which is why the checkbox drew a pill reading "unfiled"
   * from a field called `inbox` and sent `?inbox=true` to fill it — the very collision the label
   * change was meant to end, one layer down. `unfiled` is the only spelling the server takes:
   * `inbox` survived as a deprecated alias through 1.36.0 and 1.37.0 and was removed in 1.38.0.
   */
  unfiled?: boolean;
  includeDone?: boolean;
  /** Blocked by an open predecessor, or startable. Derived, never read from `status`. */
  blocked?: boolean;
  /**
   * Somebody outside the app has the ball.
   *
   * The server answers this one through `listWaitingOn`, which also decides the order — longest
   * silent first. A caller that re-sorted the result would be the second definition of
   * "days silent", so the rail renders the order it is given.
   */
  waiting?: boolean;
  /**
   * With `waiting`, only the promises already broken.
   *
   * A flag rather than a date, because "the promised day has passed" is a question about the
   * account's calendar day and the server is where the zone is read.
   */
  overdue?: boolean;
  /** A single day, so the "Planned today" count can be opened. */
  scheduledDate?: string;
  /** The container this belongs to. The server has accepted it since Phase 6. */
  projectId?: string;
  /**
   * Labels a task must carry.
   *
   * The app asked people to file with labels and had no way to retrieve by one, so the text box
   * was the only approximation — and it searches descriptions too, which made "Phase 1" match
   * "Phase 13" and everything that mentioned phase 1 in passing.
   */
  labelIds?: string[];
  /** `all` by default: across groups the question is "Phase 1 *and* bug". */
  labelMatch?: 'any' | 'all';
  /**
   * The deadline filter the panel offers, as a day in the account's zone — the named day
   * included.
   *
   * This was `dueBefore`, an instant, and the panel built it with `setHours(23, 59, 59, 999)` on
   * a browser `Date`: the laptop's midnight, on the laptop's date. The server has the account's
   * zone and the browser does not, so the day goes over the wire and the cutoff is resolved
   * there. `dueBefore` is still on the REST door for a genuine instant cutoff; it is not here,
   * because no screen asks one.
   */
  dueOnOrBefore?: string;
  /** At or above this priority. */
  minPriority?: number;
  /**
   * Late by the same rule the rows are drawn with: a deadline that has passed, or a plan for a day
   * that has and no deadline. The value is the local day. See `late` on `ListTasksFilter`.
   */
  late?: string;
  /** Whether a deadline is set at all — no cutoff can express "no date". */
  hasDue?: boolean;
  /** Ordering. `due` puts the soonest deadline first and undated work last. */
  sort?: 'default' | 'due' | 'priority' | 'newest' | 'oldest';
  /**
   * Whose work. `mine` on the server by default, which is why joining a project does not empty
   * somebody else's list onto your screen.
   *
   * The screens do not offer this yet — the scope switcher is Phase 14 — but the field is here so
   * that adding it is a UI change rather than a change to the client's contract.
   */
  scope?: 'mine' | 'project' | 'all';
}

// --- calls ----------------------------------------------------------------

export const getMe = () => apiFetch<Me>('/api/me');
export const getHealth = () => apiFetch<Health>('/api/health');

export function listTasks(filter: TaskFilter = {}) {
  const params = new URLSearchParams();
  if (filter.status) {
    for (const s of Array.isArray(filter.status) ? filter.status : [filter.status]) {
      params.append('status', s);
    }
  }
  if (filter.scope) params.set('scope', filter.scope);
  if (filter.goalId) params.set('goal_id', filter.goalId);
  if (filter.includeSubgoals) params.set('include_subgoals', 'true');
  if (filter.q) params.set('q', filter.q);
  if (filter.unfiled) params.set('unfiled', 'true');
  if (filter.blocked !== undefined) params.set('blocked', String(filter.blocked));
  if (filter.waiting !== undefined) params.set('waiting', String(filter.waiting));
  if (filter.overdue !== undefined) params.set('overdue', String(filter.overdue));
  if (filter.scheduledDate) params.set('scheduled_date', filter.scheduledDate);
  if (filter.projectId) params.set('project_id', filter.projectId);
  if (filter.includeDone) params.set('include_done', 'true');
  for (const id of filter.labelIds ?? []) params.append('label_ids', id);
  if (filter.labelMatch) params.set('label_match', filter.labelMatch);
  if (filter.dueOnOrBefore) params.set('due_on_or_before', filter.dueOnOrBefore);
  if (filter.minPriority !== undefined) params.set('min_priority', String(filter.minPriority));
  if (filter.late) params.set('late', filter.late);
  if (filter.hasDue !== undefined) params.set('has_due', String(filter.hasDue));
  if (filter.sort && filter.sort !== 'default') params.set('sort', filter.sort);
  const qs = params.toString();
  return apiFetch<Task[]>(`/api/tasks${qs ? `?${qs}` : ''}`);
}

export const getTask = (id: string) => apiFetch<Task>(`/api/tasks/${id}`);

/**
 * Only the fields the server will accept, the way `GoalEdit` is for goals.
 *
 * These were `Partial<Task>`, which is a *response* shape: it let `id`, `createdAt`,
 * `isBlocked`, `waitingDays` and every other server-computed field be sent to a body schema that
 * does not declare them. Nothing in the app did — every call site passes an object literal — but
 * the type invited the one thing that would break a screen now that `POST /api/tasks` refuses
 * what it does not declare rather than dropping it: reading a task, changing a field and sending
 * the whole thing back. `Partial<Task>` compiled that. This does not.
 */
export interface TaskEdit {
  title?: string;
  descriptionMd?: string;
  status?: TaskStatus;
  priority?: number;
  dueAt?: string | null;
  scheduledDate?: string | null;
  estimateMinutes?: number | null;
  complexityPoints?: number | null;
  parentTaskId?: string | null;
  projectId?: string | null;
  goalIds?: string[];
  labelIds?: string[];
  color?: string | null;
  sortOrder?: number;
}

export const createTask = (body: TaskEdit & { title: string }) =>
  send<Task>('POST', '/api/tasks', body);
export const updateTask = (id: string, body: TaskEdit) =>
  send<Task>('PATCH', `/api/tasks/${id}`, body);
/**
 * Completing answers the task *and* the occurrence it created, since `01a0809a-11db`.
 *
 * Every caller in this app discards the value and invalidates instead — checked when the shape
 * changed — so this type exists to stay honest rather than because something reads it. A caller
 * that wants to point at the new occurrence has its id here instead of searching by a title
 * recurring tasks share.
 */
export const completeTask = (id: string) =>
  send<{ task: Task; nextOccurrence: Task | null }>('POST', `/api/tasks/${id}/complete`);

/**
 * The same piece of work again — everything but the status, which is `inbox`.
 *
 * The copy is linked to the original with `relates`, so the two can be seen to be the same thing.
 * Recurrence rules and attachments are not copied; a copied rule would spawn a second chain of
 * successors for ever.
 */
export const cloneTask = (id: string) => send<Task>('POST', `/api/tasks/${id}/clone`);

/**
 * One request for a whole selection.
 *
 * `done` is deliberately not in `BulkPatch`. Completion runs a recurring task's successor, the
 * habit entry and the timeline block, all of which live in `completeTask`; the endpoint rejects
 * the status outright rather than quietly writing the row and skipping the rest.
 *
 * `skipped` carries ids the server would not write. `not_found` covers both "no such task" and
 * "not yours" — the same answer to both is what keeps the response from being an existence oracle.
 */
export interface BulkPatch {
  status?: Exclude<TaskStatus, 'done'>;
  priority?: number;
  dueAt?: string | null;
  scheduledDate?: string | null;
  estimateMinutes?: number | null;
  goalIds?: string[];
  labelIds?: string[];
  /** Who holds each task; `null` releases it. See `assigneeUserId` on the service's patch. */
  assigneeUserId?: string | null;
}

export interface BulkResult {
  updated: number;
  skipped: { id: string; reason: 'not_found' | 'blocked' }[];
}

export const bulkUpdateTasks = (ids: string[], patch: BulkPatch) =>
  send<BulkResult>('PATCH', '/api/tasks/bulk', { ids, patch });
export const deleteTask = (id: string) => send<void>('DELETE', `/api/tasks/${id}`);
export const listSubtasks = (id: string) => apiFetch<Task[]>(`/api/tasks/${id}/subtasks`);

export const getGoalTree = () => apiFetch<GoalTreeNode[]>('/api/goals/tree');
/**
 * Only the fields the server will accept.
 *
 * `Partial<Goal>` was wrong here: it let `id`, `path` and `depth` be sent to a body
 * schema that does not have them, so a typo compiled and failed at runtime.
 */
export interface GoalEdit {
  title?: string;
  descriptionMd?: string;
  status?: GoalStatus;
  targetDate?: string | null;
  color?: string | null;
  sortOrder?: number;
}

export const createGoal = (body: GoalEdit & { title: string; parentGoalId?: string | null }) =>
  send<Goal>('POST', '/api/goals', body);
export const updateGoal = (id: string, body: GoalEdit) =>
  send<Goal>('PATCH', `/api/goals/${id}`, body);
/**
 * Re-parent a goal, rewriting its whole subtree's paths.
 *
 * The route has existed since Phase 1 and had no client function at all — which is why it never
 * appeared in the sweep for unused client functions in Phase 14. So a goal created at the top
 * could never become a sub-goal of another, and hierarchies are discovered rather than designed:
 * you realise "run a Spartan trifecta" belongs under "get fit" only once both exist.
 */
export const moveGoal = (id: string, parentGoalId: string | null) =>
  send<Goal>('POST', `/api/goals/${id}/move`, { parentGoalId });

export const deleteGoal = (id: string) =>
  send<{ deletedGoals: number }>('DELETE', `/api/goals/${id}`);

export const listChecklist = (taskId: string) =>
  apiFetch<ChecklistItem[]>(`/api/tasks/${taskId}/checklist`);

/**
 * A reminder set on a task, sent or not.
 *
 * `deliveredAt: null` means it is set and still waiting. The bell deliberately does not carry
 * those — `visible()` keeps a future `deliverAt` out of the notification list and out of the badge
 * — so this is the only place a pending reminder is visible at all.
 */
export interface TaskReminder {
  id: string;
  title: string;
  body: string;
  deliverAt: string;
  deliveredAt: string | null;
  readAt: string | null;
  createdAt: string;
}
export const listTaskReminders = (taskId: string) =>
  apiFetch<TaskReminder[]>(`/api/tasks/${taskId}/reminders`);
export const addChecklistItem = (taskId: string, label: string) =>
  send<ChecklistItem>('POST', `/api/tasks/${taskId}/checklist`, { label });
export const updateChecklistItem = (
  taskId: string,
  itemId: string,
  body: { label?: string; done?: boolean },
) => send<ChecklistItem>('PATCH', `/api/tasks/${taskId}/checklist/${itemId}`, body);
export const deleteChecklistItem = (taskId: string, itemId: string) =>
  send<void>('DELETE', `/api/tasks/${taskId}/checklist/${itemId}`);
/** Take it on, let it go, hand it over. One call each, at level `assign`. */
export const claimTask = (id: string) => send<Task>('POST', `/api/tasks/${id}/claim`);
export const releaseTask = (id: string) => send<Task>('POST', `/api/tasks/${id}/release`);

/**
 * Handing the ball to somebody who will never log in, and the two things you do afterwards.
 *
 * Three calls at the same `assign` rung as claim and release, and for the same reason: they
 * change who is expected to act next, not what the task is. Each returns the whole task, so a
 * surface redraws the row it already holds rather than refetching to find out what it now says.
 *
 * There is deliberately no PATCH here. The five columns are unpatchable on the server, because a
 * patch would move them without the audit row and the thread entry that make a chase history
 * readable.
 */
export const setWaitingOn = (
  id: string,
  body: { waitingOn: string; promisedBy?: string | null; chaseAfterDays?: number | null },
) => send<Task>('PUT', `/api/tasks/${id}/waiting-on`, body);
export const logChase = (id: string) => send<Task>('POST', `/api/tasks/${id}/waiting-on/chase`);
export const resolveWaitingOn = (id: string) => send<Task>('DELETE', `/api/tasks/${id}/waiting-on`);

/** Turn a step into a subtask: one call, one transaction, nothing retyped. */
export const promoteChecklistItem = (taskId: string, itemId: string) =>
  send<Task>('POST', `/api/tasks/${taskId}/checklist/${itemId}/promote`);
export const reorderChecklist = (taskId: string, orderedIds: string[]) =>
  send<ChecklistItem[]>('POST', `/api/tasks/${taskId}/checklist/reorder`, { orderedIds });

export const listComments = (taskId: string) =>
  apiFetch<Comment[]>(`/api/tasks/${taskId}/comments`);
export const addComment = (taskId: string, bodyMd: string) =>
  send<Comment>('POST', `/api/tasks/${taskId}/comments`, { bodyMd });

export const listLinks = (taskId: string) => apiFetch<TaskLink[]>(`/api/tasks/${taskId}/links`);
export const createLink = (taskId: string, toTaskId: string, type: 'blocks' | 'relates') =>
  send<{ id: string }>('POST', `/api/tasks/${taskId}/links`, { toTaskId, type });
export const deleteLink = (taskId: string, linkId: string) =>
  send<void>('DELETE', `/api/tasks/${taskId}/links/${linkId}`);

// --- templates (Phase 1b) --------------------------------------------------

export type OnCreateMode = 'none' | 'ask' | 'block_time' | 'reminders_only';

export interface Template {
  id: string;
  name: string;
  icon: string | null;
  kind: 'single' | 'bundle';
  titlePattern: string;
  descriptionMd: string;
  goalId: string | null;
  defaultEstimateMinutes: number | null;
  defaultComplexityPoints: number | null;
  defaultPriority: number;
  recurrenceRule: string | null;
  recurrenceText: string | null;
  onCreate: OnCreateMode;
  reminderOffsets: number[];
  preferredWeekdays: number[];
  preferredWindows: unknown;
  /** Completing a task from this template logs that habit automatically. */
  linkedHabitId: string | null;
  /** How much one of these is worth to that habit. 1 unless the template says otherwise. */
  habitContribution: number;
  /** Whether a bundle's members block each other in sequence. */
  chainMembers: boolean;
  /** Labels every task this template creates will carry. */
  labelIds: string[];
  useCount: number;
  lastUsedAt: string | null;
  createdAt: string;
}

export interface TemplateItem {
  id: string;
  kind: 'checklist' | 'task';
  label: string;
  estimateMinutes: number | null;
  /** Set when a `task` item is another template rather than a bare label. */
  memberTemplateId: string | null;
  sortOrder: number;
}

interface TemplateVariant {
  id: string;
  name: string;
  descriptionMd: string | null;
  sortOrder: number;
}

export interface TemplateDetail {
  template: Template;
  items: TemplateItem[];
  variants: TemplateVariant[];
}

export interface InstantiateResult {
  tasks: Task[];
  reminderIds: string[];
  onCreate: OnCreateMode;
  planningHints?: { preferredWeekdays: number[]; preferredWindows: unknown };
}

export const listTemplates = () => apiFetch<Template[]>('/api/templates');
export const getTemplate = (id: string) => apiFetch<TemplateDetail>(`/api/templates/${id}`);
export const createTemplate = (body: Record<string, unknown>) =>
  send<TemplateDetail>('POST', '/api/templates', body);
export const updateTemplate = (id: string, body: Record<string, unknown>) =>
  send<TemplateDetail>('PATCH', `/api/templates/${id}`, body);
export const deleteTemplate = (id: string) => send<void>('DELETE', `/api/templates/${id}`);
export const instantiateTemplate = (id: string, body: Record<string, unknown> = {}) =>
  send<InstantiateResult>('POST', `/api/templates/${id}/instantiate`, body);
export const buildRecurrencePreset = (body: Record<string, unknown>) =>
  send<{ rule: string; text: string }>('POST', '/api/templates/recurrence-preset', body);

export interface AttachmentRow {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  uploadedAt: string | null;
}

export const listTaskAttachments = (taskId: string) =>
  apiFetch<AttachmentRow[]>(`/api/tasks/${taskId}/attachments`);

export async function uploadAttachment(taskId: string, file: File): Promise<AttachmentRow> {
  const reserved = await send<{ attachment: AttachmentRow; uploadUrl: string }>(
    'POST',
    `/api/tasks/${taskId}/attachments`,
    {
      filename: file.name,
      mimeType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
    },
  );
  const put = await fetch(reserved.uploadUrl, {
    method: 'PUT',
    headers: { 'content-type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!put.ok) throw new ApiError(put.status, 'upload_failed', 'Upload failed');
  return send<AttachmentRow>('POST', `/api/attachments/${reserved.attachment.id}/confirm`);
}

export const deleteAttachment = (id: string) => send<void>('DELETE', `/api/attachments/${id}`);

export async function downloadAttachment(id: string): Promise<{ url: string; filename: string }> {
  return apiFetch<{ url: string; filename: string; expiresAt: string }>(
    `/api/attachments/${id}/download`,
  );
}

// --- API tokens (Phase 2) --------------------------------------------------

export interface TokenSummary {
  id: string;
  name: string;
  tokenPrefix: string;
  scopes: string[];
  /** The projects this token reaches, or `null` for the whole account. */
  projectIds: string[] | null;
  expiresAt: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

/**
 * The boxes the mint form offers — **not** the server's vocabulary, and deliberately short of it.
 *
 * `accounts:read` and `accounts:write` exist server-side and are not here. They are inert on every
 * account the instance owner has not granted a `provisioning_quota` to, in SQL, by hand: a ticked
 * box that produces a credential answering 403 to the only thing it names is worse than no box.
 * Whoever can grant a quota can also mint the token — `packages/core/src/scripts/mint-token.ts`
 * takes `--scopes` and already reaches the whole vocabulary — so the capability is usable without
 * putting a control in front of everybody it can never work for.
 *
 * Reopen this the day a quota can be granted from inside the app. Then the box has an audience.
 *
 * A token that already holds them renders fine: the list below is what the *form* offers, and the
 * panel prints `token.scopes` as the server reports it.
 */
export const SCOPE_LIST = [
  'todos:read',
  'todos:write',
  'goals:read',
  'goals:write',
  'habits:read',
  'habits:write',
  'projects:read',
  'projects:write',
  'labels:read',
  'labels:write',
  'notes:read',
  'notes:write',
  'calendar:read',
  'calendar:write',
  'delete',
] as const;

/**
 * The scopes a token limited to named projects may hold — the families whose rows carry a project.
 *
 * A convenience for the mint form, not the rule: `createToken` refuses the combination server-side
 * and says why, and that refusal is what the form shows if this list is ever wrong. Kept beside
 * `SCOPE_LIST`, which is already the client's copy of the same vocabulary.
 */
export const NARROWABLE_SCOPES: readonly string[] = SCOPE_LIST.filter((scope) => {
  const [family, verb] = scope.split(':');
  return verb === undefined || ['todos', 'notes', 'labels', 'projects'].includes(family!);
});

export const listTokens = () => apiFetch<TokenSummary[]>('/api/tokens');
export const createApiToken = (body: {
  name: string;
  scopes: string[];
  /** Omit or null for the whole account; a list limits the token to those projects. */
  projectIds?: string[] | null;
  ttlDays?: number | null;
}) =>
  send<{ token: TokenSummary; plaintext: string; summary: string[] }>('POST', '/api/tokens', body);
export const revokeApiToken = (id: string) => send<TokenSummary>('DELETE', `/api/tokens/${id}`);

// --- the OAuth door (Phase 89) ---------------------------------------------

export interface PendingAuthorization {
  requestId: string;
  clientName: string;
  redirectUri: string;
  requestedScopes: string[];
  /** What the screen arrives with ticked: everything asked for, less `delete`. */
  defaultScopes: string[];
  expiresAt: string;
}

/** The pending request the browser was sent here about. Session-only, like the tokens screen. */
export const getPendingAuthorization = (requestId: string) =>
  apiFetch<PendingAuthorization>(`/api/oauth/consent?request=${encodeURIComponent(requestId)}`);

/**
 * Allow or deny, and go where the answer says.
 *
 * `redirectTo` is always the client's own registered callback — carrying a code on an allow and
 * `error=access_denied` on a deny — so both answers end the same way: the browser leaves this app
 * and returns to whatever sent it here.
 */
export const decideAuthorization = (body: {
  request: string;
  decision: 'allow' | 'deny';
  scopes?: string[];
}) => send<{ redirectTo: string }>('POST', '/api/oauth/consent', body);

// --- habits (Phase 3) ------------------------------------------------------

export type HabitPolarity = 'positive' | 'negative';
export type HabitPeriod = 'day' | 'week' | 'month';
type HabitState =
  | 'met'
  | 'on_track'
  | 'behind'
  | 'at_limit'
  | 'breached'
  | 'clean'
  /** A ceiling you take readings of, with no reading in the period. Not a limit held. */
  | 'unmeasured'
  /**
   * The user stood this habit down. Not the same as `unmeasured`: that one means nobody was
   * here, this one means nobody was asked.
   */
  | 'paused';

export type HabitAggregation = 'sum' | 'latest' | 'mean';

/**
 * How far past a ceiling still counts as holding the line, as a fraction of the ceiling.
 *
 * The browser's copy of `LIMIT_MARGIN` in `@planner/core`, restated because the bundle
 * deliberately does not depend on core and a constant this small is cheaper than pulling Drizzle
 * into the browser. `state` is `'breached'` at a gram over and at double, so without a threshold
 * every surface here paints 80.84 kg against a ceiling of 80 the colour of giving up.
 *
 * `isMeasurement` used to be restated beside it for the same reason, and that one was not safe to
 * copy: it is a *judgement* about a habit, not a constant, and the browser's answer drifted from
 * the service's for every ceiling stored as a sum. The service says `isReading` now.
 */
const LIMIT_MARGIN = 0.05;

/** Over the line, but only just — worth a warning, not the failure colour. */
export const isMarginalBreach = (p: { state: string; overshoot: number }): boolean =>
  p.state === 'breached' && p.overshoot > 0 && p.overshoot <= LIMIT_MARGIN;

export interface Habit {
  id: string;
  name: string;
  description: string;
  polarity: HabitPolarity;
  targetCount: number;
  period: HabitPeriod;
  unit: string | null;
  goalId: string | null;
  weekStartsOn: number;
  preferredWeekdays: number[];
  color: string | null;
  icon: string | null;
  activeFrom: string | null;
  activeTo: string | null;
  archivedAt: string | null;
  createdAt: string;
  /** How a period figure is read: a total to reach, the latest reading, or the average. */
  aggregation: HabitAggregation;
}

export interface HabitProgress {
  habit: Habit;
  actual: number;
  target: number;
  ratio: number;
  percent: number;
  state: HabitState;
  /** How far past a limit, as a fraction of it — see `LIMIT_MARGIN`. */
  overshoot: number;
  onTrack: boolean;
  verdict: HabitVerdict;
  isDrifting: boolean;
  isAbstinence: boolean;
  /**
   * True for a ceiling you **read** (blood pressure, body fat) rather than one you **tally**.
   *
   * Said by the service. This screen used to work it out from `aggregation` with a local copy of
   * `isMeasurement`, and that flag is wrong about exactly the habits the distinction is for: the
   * account that logs most stores six of its readings as `sum`, so every tile, ring and chart went
   * on treating a body weight as a workload.
   */
  isReading: boolean;
  /**
   * Both absent while the habit is paused — the server omits them rather than sending zero,
   * because a pace and a shortfall are debts against a target that is not in force. A surface
   * printing either has to say what it does when there is none.
   */
  expectedByNow?: number;
  remaining?: number;
  elapsed: number;
  bounds: { start: string; end: string; length: number };
  byDay: { day: string; value: number; recorded: boolean }[];
  /** The last N periods, one cell each — see `HabitStripCell` in core. */
  strip: {
    key: string;
    met: boolean;
    total: number;
    isCurrent: boolean;
    applicable: boolean;
    /** Nothing recorded, and for this habit that is an absence rather than a zero. */
    unmeasured: boolean;
    /** Stood down before this period — the third reason `applicable` is false. */
    paused: boolean;
  }[];
  cleanDayCount?: number;
  /** Whether today is one of them — what the card's " including today" is keyed on. */
  cleanDayCountIncludesToday?: boolean;
  /** The amount usually logged, or null before this habit has ever been logged. */
  typicalValue: number | null;
  /**
   * Periods at target in a row, and the best such run.
   *
   * On the status row rather than only inside the history panel: reaching a streak used to mean
   * expanding the card, so eleven habits were eleven clicks and eleven requests.
   */
  currentRun: number;
  bestRun: number;
  /** How many periods both runs were counted over — said by the service, see `runFigure`. */
  runWindow: number;
  /** Measurements only: where the latest reading sits between its worst and the target. */
  band: MeasurementBand | null;
  /**
   * The day anything was last recorded for this habit, or null — said by the service. On a reading
   * `habitFigure` prints it beside the figure whenever it is not the day judged: a distance on
   * today's rows (`8 days ago`), the calendar day on a past day's (`on 5 Oct`), with no period of
   * grace.
   */
  lastReadOn: string | null;
  /**
   * For a ceiling you read, the latest reading on record however old, taken on `lastReadOn`; null
   * otherwise. `band` places this same reading when the history window holds it and is null when
   * it is older. What a row states for a period nobody has read, where `actual` is 0.
   */
  lastReading: number | null;
}

/**
 * Where a reading sits between the worst it has been and the target.
 *
 * `ratio` is `actual / target` capped at 1, which fills a ring at 78.84 kg against a target of 78
 * exactly as it fills it at 300 kg.
 */
interface MeasurementBand {
  /** The least good reading on record. */
  worst: number;
  target: number;
  /** 0 at `worst`, 1 at or past the target. */
  position: number;
  /** Positive means not there yet, whichever direction the habit runs. */
  toTarget: number;
}

export const listHabits = () => apiFetch<Habit[]>('/api/habits');
export const getHabitsStatus = (opts: { offTrack?: boolean; includePaused?: boolean } = {}) => {
  const params = new URLSearchParams();
  if (opts.offTrack) params.set('off_track', 'true');
  if (opts.includePaused) params.set('include_paused', 'true');
  const qs = params.toString();
  return apiFetch<HabitProgress[]>(`/api/habits/status${qs ? `?${qs}` : ''}`);
};
export const createHabit = (body: Record<string, unknown>) =>
  send<Habit>('POST', '/api/habits', body);
export const updateHabit = (id: string, body: Record<string, unknown>) =>
  send<Habit>('PATCH', `/api/habits/${id}`, body);
export const deleteHabit = (id: string) => send<void>('DELETE', `/api/habits/${id}`);
export const logHabit = (id: string, body: Record<string, unknown> = {}) =>
  send<{ id: string }>('POST', `/api/habits/${id}/entries`, body);
/** One period of a habit's history: what was logged and whether the target was met. */
export interface HabitPeriodResult {
  bounds: { start: string; end: string; length: number };
  total: number;
  met: boolean;
  isCurrent: boolean;
  /**
   * Nothing was recorded, and for this habit that is an absence rather than a zero.
   *
   * Without it a bar could only ask `met`, and `met: false` is what a genuine miss says too — so
   * a morning nobody weighed drew in the miss colour and titled itself "0 of 50 — missed". The
   * domain has known the difference since Phase 51; it was being dropped by the response schema.
   */
  unmeasured: boolean;
  /** Whether this period counts towards the met-rate — see the route schema for why it is here. */
  applicable: boolean;
  /**
   * The habit was paused before this period began.
   *
   * The third reason `applicable` is false, and the chart had been reading the other two and
   * assuming this one away: every bar after a pause was titled "before this habit started".
   */
  paused: boolean;
}

export interface HabitTrend {
  direction: 'rising' | 'flat' | 'falling' | 'unknown';
  /**
   * The direction read against the habit's target, which is the only readable form of it.
   *
   * "-50%" on a drink limit is the best news on the page and "-33%" on a training habit is the
   * worst; both were printed as a bare signed percentage in the same colour. The judgement is
   * made in the domain so REST and MCP agree — the tile only words it.
   */
  verdict: 'better' | 'worse' | 'steady' | 'unknown';
  recent: number;
  earlier: number;
  /** Null when the earlier half was zero — a direction, but not a percentage. */
  changeRatio: number | null;
  /**
   * The same change in the habit's own units, and the days it happened over.
   *
   * A percentage of a body weight is always small — 81.235 → 79.985 is −1.5%, inside the
   * tolerance — so a measurement is judged and worded in kilograms instead.
   */
  changeAbsolute: number;
  spanDays: number;
  /** Which rule decided `direction`, and so how to word it. */
  basis: 'relative' | 'absolute';
  halfLength: number;
}

export interface HabitHistory {
  periods: HabitPeriodResult[];
  periodsMet: number;
  periodsConsidered: number;
  /** Null when no period has completed yet, which is not the same as zero. */
  rate: number | null;
  currentRun: number;
  bestRun: number;
  /**
   * The day the record starts, which is not the day the window starts.
   *
   * The tile used to work this out from `periods` — the first applicable one — and for a habit
   * older than the window that is the chart's left edge, so it read "of 11 weeks tracked since
   * 22 Jun" on a habit first logged on 25 May.
   */
  trackedSince: string;
  /** A target the history says would be met, or null when there is nothing useful to say. */
  suggestion: {
    /** Null on a `restart` over an account that was away: no number, and no button. */
    suggested: number | null;
    direction: 'lower' | 'higher';
    typical: number;
    periodsMet: number;
    periodsConsidered: number;
    /** `observed` is measured from behaviour; `restart` is a starting point, not a measurement. */
    basis: 'observed' | 'restart';
    /** Which of the three situations produced it — `direction` alone is ambiguous. */
    rationale: 'missing' | 'outgrown' | 'comfortable';
    /** The last day logged before the period in progress — set exactly when `suggested` is null. */
    since: string | null;
  } | null;
  /** Which way it is going — a met-rate cannot rise above what the target allows. */
  trend: HabitTrend;
  /**
   * Everything ever logged. A floor that only goes up.
   *
   * Null for a measurement, where it is seventy mornings on a scale added together — the tile
   * read "All time: 5640.26 kg". Those get `measurement` instead.
   */
  lifetimeTotal: number | null;
  /** Only for a measurement: the record a total and a met-rate cannot describe. */
  measurement: MeasurementRecord | null;
}

/** Where a measurement habit is, where it has been, and how far it still has to go. */
interface MeasurementRecord {
  latest: number;
  latestOn: string;
  /** The best on record, in whichever direction the habit runs. */
  best: number;
  bestOn: string;
  first: number;
  firstOn: string;
  /** Signed: negative is downwards, which for a ceiling is progress. */
  changeSinceStart: number;
  /** Positive means not there yet, whichever direction the habit runs. */
  toTarget: number;
  daysAtTarget: number;
  daysMeasured: number;
}

export interface HabitEntry {
  id: string;
  occurredOn: string;
  value: number;
  note: string | null;
  source: string;
  taskId: string | null;
  createdAt: string;
}

export const getHabitHistory = (id: string, periods?: number) =>
  apiFetch<HabitHistory>(`/api/habits/${id}/history${periods ? `?periods=${periods}` : ''}`);

export const listHabitEntries = (id: string, from: string, to: string) =>
  apiFetch<HabitEntry[]>(`/api/habits/${id}/entries?from=${from}&to=${to}`);

export const deleteHabitEntry = (habitId: string, entryId: string) =>
  send<void>('DELETE', `/api/habits/${habitId}/entries/${entryId}`);

/**
 * Takes back the most recent manual entry on a day. Automatic entries are left alone.
 *
 * `on` is optional, and omitting it is the right call for anything that means "today": the route
 * then answers with `localDay(now, user.timezone)`, the account's own day. A caller that computes
 * today for itself is a second clock that has to agree with the server's, and the Habits screen's
 * did not — it sent the *browser's* day, so for an account in another zone the undo removed
 * yesterday's entry. Pass a day only when the screen is genuinely pointed at one, as the day view
 * is.
 */
export const undoHabitLog = (id: string, on?: string) =>
  send<{ undone: boolean }>('DELETE', `/api/habits/${id}/entries/last${on ? `?on=${on}` : ''}`);
export const getHabitHeatmap = (id: string, from: string, to: string) =>
  apiFetch<HeatmapDay[]>(`/api/habits/${id}/heatmap?from=${from}&to=${to}`);

/**
 * A day the year grid has something to say about. `value` is null when nothing was logged on the
 * habit; `used` is whether the account was here at all. A day absent from the list had neither.
 */
interface HeatmapDay {
  day: string;
  value: number | null;
  used: boolean;
}

// --- day timeline (Phase 4) ------------------------------------------------

type BlockType = 'event' | 'task_block' | 'habit_block' | 'reminder';

export interface TimelineEvent {
  id: string;
  title: string;
  description: string;
  location: string | null;
  startsAt: string;
  endsAt: string;
  timezone: string;
  isAllDay: boolean;
  blockType: BlockType;
  origin: string;
  taskId: string | null;
  habitId: string | null;
  /** The project the block belongs to, inherited from its task when it was placed. */
  projectId: string | null;
  completedAt: string | null;
  /**
   * Set when the entry was deleted in Google. The server keeps it on the day for a week
   * afterwards so the deletion can be *seen* — a calendar entry vanishing silently is
   * indistinguishable from a sync bug — which only works if this is drawn differently.
   */
  deletedAt: string | null;
}

export interface Conflict {
  id: string;
  title: string;
  startsAt: string;
  endsAt: string;
}

export interface DayPlan {
  date: string;
  timezone: string;
  dayStart: string;
  dayEnd: string;
  events: TimelineEvent[];
  tasks: Task[];
  /** Work that could be pulled into this day. The shortlist drags from here. */
  candidates: Task[];
  habits: DayHabit[];
  budget: DayBudget;
}

/**
 * How much room the day has, against how much work is pointed at it.
 *
 * Computed by the service rather than summed here, so the week canvas's column headers and this
 * screen's bar cannot arrive at two different answers about the same Tuesday. §4's decision is
 * warn, never block: nothing on this object may hide a row or refuse a drag.
 */
export interface DayBudget {
  /**
   * Unoccupied minutes inside the account's own day window — and, for the account's today, only
   * the ones still ahead. The past is not room.
   */
  freeMinutes: number;
  /**
   * What is left to place: each row's estimate less the minutes already blocked out for it,
   * summed over `candidates` and nothing else.
   *
   * The set matters, because the bar is drawn over the shortlist and the fold line under it is a
   * running sum down the same rows. Work in `tasks` that the shortlist does not offer is not in
   * here, so the bar and the rows beneath it can never say different things about the same day.
   * It is not a total for `tasks`, and no reading of it should be: neither list contains the
   * other, in either direction.
   */
  shortlistedMinutes: number;
  /**
   * How many rows of `candidates` carry no estimate at all. They count as zero minutes, so the figure beside
   * this one is a floor — which is why the bar says this number rather than swallowing it.
   */
  unestimated: number;
}

/** A habit as the day view needs it: enough to log against, not only to read. */
export interface DayHabit {
  id: string;
  name: string;
  icon: string | null;
  unit: string | null;
  /* The same union the habits screen uses, not `string` — the strip needs to know the direction
     to tell a partial period from a slip, and `string` let a bare value through. */
  polarity: HabitPolarity;
  period: HabitPeriod;
  actual: number;
  target: number;
  remaining: number;
  /*
    The same union the habits screen uses, not `string`.

    Typed loosely, this row could not share `stateLabel` with the rest of the app, so Today kept a
    second copy of the state vocabulary — and when Phase 76 taught the habits screen to say "just
    over", this one silently did not follow.
  */
  state: HabitState;
  /** How far past a limit, as a fraction of it — see `LIMIT_MARGIN`. */
  overshoot: number;
  onTrack: boolean;
  verdict: HabitVerdict;
  isAbstinence: boolean;
  /** True for a ceiling you read rather than one you tally — said by the service. */
  isReading: boolean;
  /**
   * Behind for the period, as the service judges it — the same flag the Habits screen's banner and
   * Behind filter read. Today's order ranks on it through the shared `undoneRank`, so the two
   * screens agree about what is behind by construction rather than by two derivations.
   */
  isDrifting: boolean;
  /** The day anything was last recorded, as of the day shown — see `HabitProgress.lastReadOn`. */
  lastReadOn: string | null;
  /** The latest reading, as of the day shown — see `HabitProgress.lastReading`. */
  lastReading: number | null;
  loggedOnDay: number;
  /** An entry exists on the day shown, even one of 0 — what gates the undo. */
  recordedOnDay: boolean;
  byDay: { day: string; value: number; recorded: boolean }[];
  /** The last N periods, one cell each — the same strip the Habits screen draws. */
  strip: {
    key: string;
    met: boolean;
    total: number;
    isCurrent: boolean;
    applicable: boolean;
    /** Nothing recorded, and for this habit that is an absence rather than a zero. */
    unmeasured: boolean;
    /** Stood down before this period — the third reason `applicable` is false. */
    paused: boolean;
  }[];
  /** The amount usually logged, or null before this habit has ever been logged. */
  typicalValue: number | null;
  /** How a period's figure is read. A total to reach reads nothing like a reading you take. */
  aggregation: HabitAggregation;
  /** The streak, so it is visible where the logging actually happens. */
  currentRun: number;
  bestRun: number;
  /** How many periods both runs were counted over — said by the service, see `runFigure`. */
  runWindow: number;
}

export interface FreeSlot {
  start: string;
  end: string;
  /** Whether it falls in any preferred window at all. Derived from the rank by the server. */
  preferred: boolean;
  /**
   * Which window, counting from the first: 0 is a first choice, 1 a second, `null` neither.
   *
   * A template's windows are an ordered preference — "07:00–09:00, and failing that 18:00–20:00" —
   * so the rank is what lets the picker say *why* a slot is being offered instead of leaving an
   * automatic decision unexplained.
   */
  preferenceRank: number | null;
}

export const getDayPlan = (date: string) => apiFetch<DayPlan>(`/api/day/${date}`);

/**
 * A task as the week canvas draws it.
 *
 * Not a `Task`. `/api/day/:date` sends fifty of those in `candidates` — an open finding against
 * it — and a week would repeat that in seven columns plus the pile. These eight fields are what a
 * chip renders and what `rankCandidate` reads, so the order the server gave can be checked rather
 * than trusted; opening one asks `/api/tasks/:id` for the rest.
 */
export interface WeekChip {
  id: string;
  title: string;
  estimateMinutes: number | null;
  priority: number;
  dueAt: string | null;
  scheduledDate: string | null;
  status: TaskStatus;
  /** Minutes already blocked out for this task on the column it is in. Zero in the pile. */
  plannedMinutes: number;
}

export interface WeekDay {
  date: string;
  dayStart: string;
  dayEnd: string;
  /**
   * How many hours this day actually has — 23 or 25 where the clocks moved.
   *
   * The column draws this many rows. Drawing 24 and trusting it is how a 25-hour Sunday loses its
   * last hour and a 23-hour one grows a phantom.
   */
  hours: number;
  events: TimelineEvent[];
  /** Promised to this day, or holding a block on it. Done rows included: a past day is a record. */
  tasks: WeekChip[];
  /** The same three numbers `/api/day/:date` reports for this date. */
  budget: DayBudget;
  /**
   * What is pointed at this day and not yet placed on it, over `tasks`.
   *
   * The header draws this and `budget.freeMinutes`. It deliberately does not draw
   * `budget.shortlistedMinutes`: that counts the *shortlist* for the date, which is close to the
   * same offer in all seven columns — measured on the manager persona, 37 of 61 open rows carry
   * no plan date at all — so seven columns of it would read as seven separate loads.
   */
  dated: { minutes: number; unestimated: number; count: number };
}

export interface WeekPlan {
  weekStart: string;
  weekEnd: string;
  timezone: string;
  /** Work for any day of the week, best first. Nothing already dated inside the week is here. */
  pile: WeekChip[];
  pileTruncated: boolean;
  days: WeekDay[];
}

/** Any day in the week; the answer says which Monday it was taken to mean. */
export const getWeekPlan = (weekStart: string) => apiFetch<WeekPlan>(`/api/week/${weekStart}`);

export const scheduleTask = (taskId: string, startsAt: string, durationMinutes?: number) =>
  send<{ event: TimelineEvent; conflicts: Conflict[] }>('POST', `/api/tasks/${taskId}/schedule`, {
    startsAt,
    ...(durationMinutes ? { durationMinutes } : {}),
  });

export const moveBlock = (
  eventId: string,
  body: { startsAt?: string; durationMinutes?: number; updateTaskEstimate?: boolean },
) => send<{ event: TimelineEvent; conflicts: Conflict[] }>('PATCH', `/api/events/${eventId}`, body);

export const deleteBlock = (eventId: string) => send<void>('DELETE', `/api/events/${eventId}`);

/**
 * Where a task of this length would fit.
 *
 * `fromHour`/`toHour` are passed so the search covers the user's own day rather than the
 * endpoint's 9-to-18 default — someone whose day window starts at 06:00 for the gym would
 * otherwise never be offered the slot they actually wanted.
 */
export const getFreeSlots = (
  date: string,
  duration: number,
  window?: { fromHour: number; toHour: number },
  /*
    The template the task came from, so its preferred windows mark the right slots.

    `.slot-band.is-preferred` has been styled since Phase 4 and could never fire: the server
    marks a slot preferred only when it knows the template's windows, and the browser had no way
    to say which template. So "usually in the morning" was recorded, exported, imported, honoured
    by one code path — and invisible on the only screen anybody picks a slot from.
  */
  templateId?: string | null,
) => {
  const params = new URLSearchParams({ duration: String(duration) });
  if (window) {
    params.set('from_hour', String(window.fromHour));
    params.set('to_hour', String(window.toHour));
  }
  if (templateId) params.set('template_id', templateId);
  return apiFetch<FreeSlot[]>(`/api/day/${date}/free-slots?${params.toString()}`);
};

// --- Google Calendar (Phase 5) ---------------------------------------------

export interface CalendarAccount {
  id: string;
  email: string;
  provider: string;
  syncStatus: 'connected' | 'syncing' | 'error' | 'reauth_required';
  lastError: string | null;
  lastSyncAt: string | null;
  createdAt: string;
}

export interface CalendarRow {
  id: string;
  calendarAccountId: string;
  providerCalendarId: string;
  name: string;
  color: string | null;
  timezone: string;
  isWritable: boolean;
  syncEnabled: boolean;
  isPrimary: boolean;
  lastSyncAt: string | null;
  watchExpiresAt: string | null;
}

export interface SyncResult {
  calendarId: string;
  created: number;
  updated: number;
  deleted: number;
  conflicts: number;
  fullResync: boolean;
  summary: string;
}

export interface CalendarConflict {
  id: string;
  title: string;
  startsAt: string;
  endsAt: string;
  calendarId: string | null;
}

export const listCalendarAccounts = () => apiFetch<CalendarAccount[]>('/api/calendar/accounts');
export const connectCalendarAccount = (code: string) =>
  send<CalendarAccount>('POST', '/api/calendar/accounts/google/connect', { code });
export const disconnectCalendarAccount = (id: string) =>
  send<void>('DELETE', `/api/calendar/accounts/${id}`);
export const listCalendarRows = () => apiFetch<CalendarRow[]>('/api/calendar/calendars');
export const setCalendarSync = (id: string, syncEnabled: boolean) =>
  send<CalendarRow>('PATCH', `/api/calendar/calendars/${id}`, { syncEnabled });
export const syncAllCalendars = () => send<SyncResult[]>('POST', '/api/calendar/sync');
export const listCalendarConflicts = () => apiFetch<CalendarConflict[]>('/api/calendar/conflicts');
export const resolveCalendarConflict = (id: string, keep: 'mine' | 'theirs') =>
  send<{ id: string; syncState: string }>('POST', `/api/calendar/conflicts/${id}/resolve`, {
    keep,
  });

// --- projects and notes (Phase 6) ------------------------------------------

export type ProjectType = 'trip' | 'project' | 'event';
type ProjectStatus = 'planning' | 'active' | 'done' | 'archived';
type ProjectRole = 'reporter' | 'developer' | 'maintainer' | 'owner';

export interface Project {
  id: string;
  ownerUserId: string;
  type: ProjectType;
  title: string;
  descriptionMd: string;
  startDate: string | null;
  endDate: string | null;
  status: ProjectStatus;
  color: string | null;
  timezone: string;
  metadata: unknown;
  archivedAt: string | null;
  createdAt: string;
  /** Which goals this project serves. */
  goalIds: string[];
  parentProjectId: string | null;
  path: string;
  depth: number;
}

/**
 * A project as the *list* returns it — with what is happening inside it.
 *
 * Separate from `Project` rather than an optional field on it, because the create and update
 * responses carry a bare row and an optional count is a count you have to check for at every
 * render. Only the list and the contents call answer this question, and both always do.
 */
export interface ProjectSummary extends Project {
  progress: ProjectProgress;
}

export interface Note {
  id: string;
  projectId: string | null;
  goalId: string | null;
  taskId: string | null;
  title: string;
  bodyMd: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * What is happening in a project.
 *
 * `status` here is computed from the counts beside it; `Project.status` is the hand-set column and
 * goes stale the moment anybody starts working. The screens draw this one — see `Projects.tsx`.
 */
export interface ProjectProgress {
  total: number;
  done: number;
  inProgress: number;
  dropped: number;
  parked: number;
  members: number;
  lastActivityAt: string;
  status: ProjectStatus;
}

export interface ProjectContents {
  project: Project;
  progress: ProjectProgress;
  tasks: Task[];
  events: { id: string; title: string; startsAt: string; endsAt: string }[];
  notes: Note[];
  attachments: { id: string; filename: string; sizeBytes: number }[];
  members: { userId: string; role: ProjectRole; acceptedAt: string | null }[];
}

export interface SearchHit {
  kind: 'note' | 'task' | 'goal' | 'label' | 'project' | 'habit';
  id: string;
  title: string;
  snippet: string;
  projectId: string | null;
  /** One short discriminator, so two hits with the same title are not the same row. */
  context: string;
  /** What `context` is — a date is printed through `searchContextText`, never raw. */
  contextKind: 'dueDate' | 'scheduledDate' | 'status' | 'period' | 'labelGroup' | null;
  /** Where a note lives — see the note on the service type. Null on every other kind. */
  goalId: string | null;
  taskId: string | null;
}

export interface LabelGroup {
  /** Null is personal; set makes the group and its labels the project's. */
  projectId: string | null;
  /** Its creator — the only person who may take it back from a project. */
  userId: string;
  id: string;
  name: string;
  /** One label per task from this group; a second replaces the first. */
  exclusive: boolean;
  sortOrder: number;
  createdAt: string;
}

export interface Label {
  id: string;
  groupId: string;
  /**
   * Null is personal; set is the project's — always its group's answer, never its own.
   *
   * Read it here rather than chasing the group when all you hold is a label: `labelGroupCaption`
   * takes a `LabelGroup` because this field did not reach the client until Phase 79.
   */
  projectId: string | null;
  /** For reporting rollup only — never consulted when exclusivity is checked. */
  parentLabelId: string | null;
  name: string;
  color: string | null;
  sortOrder: number;
  createdAt: string;
}

export const listLabelGroups = () => apiFetch<LabelGroup[]>('/api/label-groups');
/** Rename, reorder, or change exclusivity. Turning it on is refused when data violates it. */
export const updateLabelGroup = (
  id: string,
  body: {
    name?: string;
    exclusive?: boolean;
    sortOrder?: number;
    /** The project to share it with, or `null` to take it back — the creator's alone. */
    projectId?: string | null;
  },
) => send<LabelGroup>('PATCH', `/api/label-groups/${id}`, body);

export const createLabelGroup = (body: Record<string, unknown>) =>
  send<LabelGroup>('POST', '/api/label-groups', body);
export const listLabels = () => apiFetch<Label[]>('/api/labels');
export const createLabel = (body: Record<string, unknown>) =>
  send<Label>('POST', '/api/labels', body);
export const updateLabel = (id: string, body: Record<string, unknown>) =>
  send<Label>('PATCH', `/api/labels/${id}`, body);
export const deleteLabel = (id: string) => send<void>('DELETE', `/api/labels/${id}`);
/**
 * The labels a task should end up carrying.
 *
 * `PUT /api/tasks/{id}/labels` **adds** and resolves exclusivity; it does not remove. That is
 * deliberate on the service — a non-exclusive group is a tag bag, and three tests say so by name —
 * but a picker sends its whole selection on every change, so a label dropped from that selection
 * has to be deleted explicitly or nothing happens. It did not, which is why deselecting a label in
 * the task detail did nothing at all: the response was a success and the label was still there.
 *
 * Pass `previous` from anywhere a selection is being edited. Omit it where there is nothing to
 * remove — creating a task, for instance.
 */
export async function setTaskLabels(
  taskId: string,
  labelIds: string[],
  previous: readonly string[] = [],
): Promise<Label[]> {
  for (const id of previous) {
    if (!labelIds.includes(id)) {
      await send<void>('DELETE', `/api/tasks/${taskId}/labels/${id}`);
    }
  }
  if (labelIds.length === 0) return [];
  return send<Label[]>('PUT', `/api/tasks/${taskId}/labels`, { labelIds });
}

export const moveProject = (id: string, parentProjectId: string | null) =>
  send<Project>('POST', `/api/projects/${id}/move`, { parentProjectId });

export const listProjectsForGoal = (goalId: string) =>
  apiFetch<Project[]>(`/api/goals/${goalId}/projects`);

export const listProjects = () => apiFetch<ProjectSummary[]>('/api/projects');
export const getProjectContents = (id: string) =>
  apiFetch<ProjectContents>(`/api/projects/${id}/contents`);
export const createProject = (body: Record<string, unknown>) =>
  send<Project>('POST', '/api/projects', body);
export const updateProject = (id: string, body: Record<string, unknown>) =>
  send<Project>('PATCH', `/api/projects/${id}`, body);
export const deleteProject = (id: string) => send<void>('DELETE', `/api/projects/${id}`);

export const listNotes = (params: Record<string, string> = {}) => {
  const qs = new URLSearchParams(params).toString();
  return apiFetch<Note[]>(`/api/notes${qs ? `?${qs}` : ''}`);
};
export const createNote = (body: Record<string, unknown>) => send<Note>('POST', '/api/notes', body);
export const updateNote = (id: string, body: Record<string, unknown>) =>
  send<Note>('PATCH', `/api/notes/${id}`, body);
export const deleteNote = (id: string) => send<void>('DELETE', `/api/notes/${id}`);

export const searchEverything = (q: string) =>
  apiFetch<SearchHit[]>(`/api/search?q=${encodeURIComponent(q)}`);

// --- reflection and notifications (Phase 7) ----------------------------------

export interface WeekReview {
  period: ReviewPeriod;
  /** Nothing was recorded in this period at all, so it carries no score. */
  noActivity: boolean;
  /** The same headline counts for the period before — the whole basis of "am I getting better". */
  previous: {
    start: string;
    end: string;
    tasksCompleted: number;
    pointsCompleted: number;
    habitsMet: number;
    habitsConsidered: number;
    /*
      The previous period read only as far as the current one has got.

      `habitsMet` above is faithful to that period's own card, so the delta matches the screen you
      reach by pressing "‹". A period still running compares against this instead, because
      comparing 23 days of this month against all 31 of last month makes the delta a statement
      about the calendar rather than about behaviour.
    */
    tasksCompletedSoFar?: number;
    pointsCompletedSoFar?: number;
    habitsMetSoFar?: number;
    soFarEnd?: string;
    /** The clock time on `soFarEnd` the task figures were cut at, `HH:MM`, while the period runs. */
    soFarTime?: string;
  };
  start: string;
  end: string;
  summary: string;
  tasksCompleted: number;
  pointsCompleted: number;
  byDay: { day: string; completed: number; habitsMet: number; habitsLogged: number }[];
  /** Today in the account's zone — see `WeekReview.today` in core. Never the browser's day. */
  today: string;
  habits: {
    id: string;
    name: string;
    target: number;
    actual: number;
    onTrack: boolean;
    verdict: HabitVerdict;
    /** At or past target, as distinct from on course for it. The headline counts this one. */
    met?: boolean;
    isAbstinence: boolean;
    /**
     * True for a ceiling you read — sent by the route all along, and only now declared here, where
     * the row needs it to state a reading rather than `actual`'s window figure.
     */
    isReading: boolean;
    /** The window the figure was measured over, so the row can say which question it answers. */
    period: 'day' | 'week' | 'month' | 'quarter';
    /**
     * Whether `target` is the habit's own figure or the calendar's arithmetic on it.
     *
     * A scaled denominator moves with the length of the month, so 79.71 in August and 72 in
     * February describe one unchanged 18/week habit — and comparing two months is the thing this
     * screen is opened to do. The row prints the constant beside the derived figure when this is
     * set; see `ReviewHabit` in the domain for the whole argument.
     */
    scaledTarget?: boolean;
    statedTarget?: number;
    statedPeriod?: 'day' | 'week' | 'month' | 'quarter';
    /**
     * For a reading, the last one taken on or before the last day reviewed, and its day — what a
     * row states where `actual` is the window's empty 0. See `ReviewHabit` in the domain.
     */
    lastReading?: number | null;
    lastReadOn?: string | null;
    /** The habit's unit and direction — what lets the row say its figure as `habitFigure` does. */
    unit?: string | null;
    polarity?: HabitPolarity;
  }[];
  habitsMet: number;
  /**
   * Habits this period cannot be judged on at all.
   *
   * Printed beside `habitsMet` and used to suppress the delta: "0 Habits at their week target −2"
   * described a week in which nothing had been measured as a week in which two things were lost.
   */
  habitsTooEarly: number;
  /** The week has not begun, so nothing in it has been achieved or missed yet. */
  notStarted: boolean;
  /**
   * The period is still running, so "behind" is a pace reading rather than a result.
   *
   * On the payload since Phase 21 and only now read by the browser — the attention box needs it
   * to word a habit that is behind without calling a live quarter a failure.
   */
  inProgress: boolean;
  /** Which weekday a week starts on here, 0–6 — see the note on the domain type. */
  weekStartsOn: number;
  goals: ReviewGoal[];
  goalsMoved: number;
  /** Last period's decision, read back where it was made. Null when there was none. */
  previousDecision: { id: string; bodyMd: string; doneTitle: string; done: boolean } | null;
  /** What to call this period's decision. Composed server-side — see the note on the domain type. */
  decisionTitle: string;
  /** What to change, as distinct from what happened. */
  attention: {
    behind: ReviewGoal[];
    stalled: ReviewGoal[];
    untouched: ReviewGoal[];
    strugglingHabits: {
      id: string;
      name: string;
      periodsMet: number;
      periodsConsidered: number;
      /*
        The unit of the two counts — the habit's own cadence.

        Required, matching the route, because the server always sends it: an optional field here is
        how four surfaces each grew a fallback for a case that cannot arrive. Note it is *not* the
        `period` on a review habit row above, which is the window that row's figure was measured
        over: a daily habit under a week review has `week` there and `day` here.
      */
      period: 'day' | 'week' | 'month';
    }[];
  };
}

export interface ReviewGoal {
  id: string;
  title: string;
  percentBefore: number;
  percentAfter: number;
  /** A habit serving this goal was logged inside the period — see `goalMoved`. */
  habitActivity?: boolean;
  paceState?: string;
  needsNextAction?: boolean;
  /** `not_started` or `ready_to_finish` — see the note on the domain type. */
  nextActionState?: string;
  daysRemaining?: number | null;
}

export interface MonthDay {
  day: string;
  inMonth: boolean;
  tasksCompleted: number;
  habitsMet: number;
  /** Daily habits in force that day — the denominator `habitsMet` is out of. */
  habitsDue: number;
  /** How many of `habitsMet` were met by logging nothing at all. See the cell's title. */
  habitsMetByAbstinence: number;
  habitsLogged: number;
  /**
   * Whether the account wrote anything on that day's page. Not a count — see the service.
   *
   * Optional on the wire: `getMonthOverview` omits it for a credential holding no `notes:read`, since
   * which days have words is a `notes` read and `false` would be a claim that credential did not pay
   * to make (`01a08c4f-0575`). This client is always a session, which holds every scope, so in
   * practice it is always present — typed honestly rather than conveniently, because the two readers
   * below already treat it as falsy-checkable and nothing here should start asserting a field the
   * server is entitled to leave out.
   */
  hasPage?: boolean;
}

export interface MonthOverview {
  month: string;
  timezone: string;
  start: string;
  end: string;
  days: MonthDay[];
}

export interface MemberRow {
  userId: string;
  email: string;
  displayName: string;
  role: 'reporter' | 'developer' | 'maintainer' | 'owner';
  acceptedAt: string | null;
}

export interface InvitationRow {
  id: string;
  email: string;
  role: 'reporter' | 'developer' | 'maintainer' | 'owner';
  /** Live, or run out. Never whether it was delivered — see the route's note. */
  status: 'pending' | 'expired';
  createdAt: string;
  expiresAt: string;
}

export interface Notification {
  id: string;
  type: string;
  title: string;
  body: string;
  payload: Record<string, unknown>;
  entityRef: string | null;
  entityTitle: string | null;
  readAt: string | null;
  createdAt: string;
}

export type ReviewPeriod = 'week' | 'month' | 'quarter';

export const getWeekReview = (week?: string, period: ReviewPeriod = 'week') => {
  const q = new URLSearchParams();
  if (week) q.set('week', week);
  if (period !== 'week') q.set('period', period);
  const s = q.toString();
  return apiFetch<WeekReview>(`/api/review/week${s ? `?${s}` : ''}`);
};

export const getMonthOverview = (month?: string) =>
  apiFetch<MonthOverview>(`/api/review/month${month ? `?month=${month}` : ''}`);

// --- the day's page ----------------------------------------------------------

export interface DayNote {
  id: string;
  title: string;
  bodyMd: string;
  day: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DayLogEntry {
  /**
   * Null where the record has no time of day — a habit entry counts towards a date, not an
   * instant. Rendered apart from the timed lines, never as midnight.
   */
  at: string | null;
  kind: 'done' | 'habit' | 'block' | 'carried';
  title: string;
  detail: string;
  taskId: string | null;
  habitId: string | null;
}

export interface DayPage {
  day: string;
  timezone: string;
  note: DayNote | null;
  log: DayLogEntry[];
  counts: { done: number; habits: number; blocks: number; carried: number };
}

export const getDayPage = (date: string) => apiFetch<DayPage>(`/api/day/${date}/page`);

export const saveDayNote = (date: string, bodyMd: string) =>
  send<DayNote>('PUT', `/api/day/${date}/note`, { bodyMd });

export const listNotifications = () =>
  apiFetch<{ notifications: Notification[]; unread: number }>('/api/notifications');

export const markNotificationRead = (id: string) =>
  send<{ notification: Notification; unread: number }>('POST', `/api/notifications/${id}/read`);

export const markAllNotificationsRead = () =>
  send<{ marked: number; unread: number }>('POST', '/api/notifications/read-all');

// --- membership and sign-in (Phase 8) ----------------------------------------

export const listMembers = (projectId: string) =>
  apiFetch<MemberRow[]>(`/api/projects/${projectId}/members`);

export const listInvitations = (projectId: string) =>
  apiFetch<InvitationRow[]>(`/api/projects/${projectId}/invitations`);

export const inviteToProject = (projectId: string, body: { email: string; role: string }) =>
  send<{ invitation: InvitationRow; link: string }>(
    'POST',
    `/api/projects/${projectId}/invitations`,
    body,
  );

export const revokeInvitation = (projectId: string, invitationId: string) =>
  send<void>('DELETE', `/api/projects/${projectId}/invitations/${invitationId}`);

export const removeMember = (projectId: string, userId: string) =>
  send<void>('DELETE', `/api/projects/${projectId}/members/${userId}`);

export const setMemberRole = (projectId: string, userId: string, role: string) =>
  send<{ userId: string; role: string }>('POST', `/api/projects/${projectId}/members`, {
    userId,
    role,
  });

export interface InvitationPreview {
  projectTitle: string;
  email: string;
  role: string;
}

/**
 * Accept an invitation as the account already signed in.
 *
 * Registration and Google login were the only paths that consumed one, so an account with a
 * password could never join a project it was invited to — the link worked and nothing read it.
 */
export const acceptInvitation = (token: string) =>
  send<{ joined: string[] }>('POST', '/api/invitations/accept', { token });

/**
 * Accept one offer the account was told about in-app, with no link involved.
 *
 * The bell's half of it: there is no mail here, so an invitation that arrives as a notification
 * has to be answerable from the notification.
 */
export const acceptInvitationById = (invitationId: string) =>
  send<{ projectId: string }>('POST', `/api/invitations/${invitationId}/accept`);

export const peekInvitation = (token: string) =>
  apiFetch<InvitationPreview>(`/api/auth/invitation/${token}`);

export const logout = () => send<{ signedOut: boolean }>('POST', '/api/auth/logout');

/** Where the browser goes to start Google sign-in. A plain link, not a fetch. */
export const googleSignInUrl = (opts: { invitation?: string; redirectTo?: string } = {}) => {
  const params = new URLSearchParams();
  if (opts.invitation) params.set('invitation', opts.invitation);
  if (opts.redirectTo) params.set('redirect_to', opts.redirectTo);
  const query = params.toString();
  return `${BASE}/api/auth/google/start${query ? `?${query}` : ''}`;
};

// --- notification channels (Phase 9) -----------------------------------------

export interface Channel {
  id: string;
  type: 'in_app' | 'telegram';
  verified: boolean;
  connectedAs: string | null;
  createdAt: string;
}

export interface ChannelPreferences {
  preferences: { notificationType: string; channelType: string; enabled: boolean }[];
  quietHours: { from: string | null; until: string | null; timezone: string };
}

export const listChannels = () => apiFetch<Channel[]>('/api/channels');

export const startTelegramVerification = () =>
  send<{ channelId: string; code: string; expiresAt: string; botUsername: string }>(
    'POST',
    '/api/channels/telegram/verify',
  );

export const revokeChannel = (id: string) => send<void>('DELETE', `/api/channels/${id}`);

export const getChannelPreferences = () =>
  apiFetch<ChannelPreferences>('/api/channels/preferences');

export const setChannelPreference = (body: {
  notificationType: string;
  channelType: string;
  enabled: boolean;
}) => send<typeof body>('POST', '/api/channels/preferences', body);

export const setQuietHours = (body: { from: string | null; until: string | null }) =>
  send<{ from: string | null; until: string | null }>('PUT', '/api/channels/quiet-hours', body);

// --- local sign-in -----------------------------------------------------------

export const loginWithPassword = (email: string, password: string, rememberMe: boolean) =>
  send<{ id: string; email: string; displayName: string }>('POST', '/api/auth/login', {
    email,
    password,
    rememberMe,
  });

export const registerAccount = (body: {
  email: string;
  password: string;
  displayName?: string;
  timezone?: string;
  rememberMe: boolean;
  invitation?: string;
}) => send<{ id: string; email: string; displayName: string }>('POST', '/api/auth/register', body);

/** Change your own name or timezone. The server is the authority on a valid zone. */
export interface StorageUsage {
  usedBytes: number;
  quotaBytes: number;
  maxFileBytes: number;
}

/*
  Phase 41: the one word every habit surface prints.

  Three screens each rendered a different boolean in the same red and green — Today showed the
  habit's own period, Reflection the review window, Habits the drift flag — and all three were
  right about different questions, which is how one habit came to be red twice and green once at
  the same instant. The server now sends a named verdict; these two functions are the only place
  the browser turns it into a colour or a word, so the surfaces cannot drift apart again.

  `too_early` is deliberately *not* green. It is the case that used to render as "on track" for a
  habit with nothing logged at all.
*/
export type HabitVerdict =
  'met' | 'on_pace' | 'behind' | 'too_early' | 'over' | 'just_over' | 'clean' | 'paused';

export const VERDICT_WORD: Record<HabitVerdict, string> = {
  met: 'at target',
  on_pace: 'on pace',
  behind: 'behind',
  too_early: 'too early to tell',
  over: 'over',
  just_over: 'just over',
  clean: 'clean',
  /*
    The word on its own. The card that also knows `activeTo` says "paused since 6 Sep" — see
    `pausedSince` — and this stays the vocabulary entry, mirrored from core's `VERDICT_LABEL`.
  */
  paused: 'paused',
};

/**
 * The pill tone. `too_early` gets none: an unjudged habit should not read as a passing grade.
 *
 * `just_over` is the same amber as being *at* the limit, which is what it is — over the line and
 * worth saying so, not a failed period. `ProgressRing` has graded it that way from `overshoot`
 * since the marginal-breach work; the pills could not, because until `just_over` existed the
 * verdict handed them the same word for a gram over and for double.
 */
export const verdictTone = (v: HabitVerdict): string =>
  // Paused gets no tone, for `too_early`'s reason twice over: nothing was judged, and nobody asked.
  v === 'paused'
    ? ''
    : v === 'met' || v === 'clean'
      ? 'ok'
      : v === 'behind' || v === 'over'
        ? 'bad'
        : v === 'just_over'
          ? 'warn'
          : v === 'on_pace'
            ? 'ok'
            : '';

export const getStorageUsage = () => apiFetch<StorageUsage>('/api/attachments/usage');

/**
 * Download the whole account.
 *
 * Fetched rather than linked, because the request needs the session cookie *and* the bearer
 * token when one is in use — a plain `<a href>` would send only the cookie and 401 for anyone
 * signed in with a token.
 */
export async function downloadExport(): Promise<void> {
  const token = getToken();
  const res = await fetch(`${BASE}/api/export`, {
    credentials: 'include',
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      error?: { code: string; message: string };
    } | null;
    throw new ApiError(
      res.status,
      body?.error?.code ?? 'unknown',
      body?.error?.message ?? 'Export failed',
    );
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `planner-export-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick: revoking synchronously can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export interface ImportResult {
  imported: Record<string, number>;
  skipped: { kind: string; count: number; reason: string }[];
}

/** Read an export back in. Additive: nothing existing is touched or matched. */
export const importAccount = (file: unknown) => send<ImportResult>('POST', '/api/import', file);

export const updateProfile = (body: { displayName?: string; timezone?: string }) =>
  send<{ displayName: string; timezone: string }>('PATCH', '/api/me', body);

export const setDayWindow = (startHour: number, endHour: number) =>
  send<{ startHour: number; endHour: number }>('PATCH', '/api/me/day-window', {
    startHour,
    endHour,
  });

/**
 * One count on the summary line. Each is also the button that opens what it counted.
 *
 * `parked` is the shelf — out of the default list, and counted so that leaving it out is not the
 * same as losing it. Never open work; see `byStatus.parked` in core.
 */
export type OverviewTile =
  'late' | 'inProgress' | 'next' | 'inbox' | 'blocked' | 'plannedToday' | 'parked';

/**
 * The shape of the open work, rather than a list of it.
 *
 * `blocked` is derived from open predecessors server-side, so it agrees with the `blocked` pill
 * on the rows below it. A count that disagrees with the list under it is worse than no count.
 */
export interface TaskOverview {
  byStatus: Record<OverviewTile, number>;
  /**
   * The filter each count was taken with, to be applied verbatim when its chip is pressed.
   *
   * The screen used to write its own filter per tile and one of them asked a different question
   * from the count: `inbox` was counted as `status = 'inbox'` and opened with `inbox: true` — "no
   * goal and no project" — so the badge read 30 and the list under it had 52 rows. Do not
   * reconstruct these here; that is the bug.
   */
  filters: Record<OverviewTile, TaskFilter>;
  /**
   * How many days late the worst late row is, or null when nothing is.
   *
   * One figure, read rather than re-derived: the Summary tile and the Today banner both show it,
   * and two screens computing "how bad is it" from the same rows is how they come to disagree.
   * Null is not 0 — a row owed today is zero days late.
   */
  oldestLateDays: number | null;
  /**
   * `byStatus.late`, split by which clock made it late; the two always sum to it.
   *
   * A passed deadline counts under `lateByDeadline` whatever the plan says, and a plan counts under
   * `lateByPlan` only for a task with no deadline. Read, never re-derived: Today's band and the Tasks
   * summary both print these two, and the double count they replace came from two components each
   * counting its own half of one pile.
   */
  lateByDeadline: number;
  lateByPlan: number;
  complexity: {
    openPoints: number;
    donePointsThisWeek: number;
    /** Null when nothing open is estimated — not the same as zero. */
    averagePoints: number | null;
    largestOpen: { id: string; title: string; points: number } | null;
  };
  /**
   * Shared projects with work in them — non-empty only when nothing open is assigned to you.
   *
   * The server decides both halves of that (see `TaskOverview.elsewhere` in core); a screen that
   * has this list has already been told it is worth showing.
   */
  elsewhere: SharedProjectPointer[];
}

/** One line of "you have nothing, but that is where the work is". */
interface SharedProjectPointer {
  projectId: string;
  title: string;
  color: string | null;
  /** The projects list's own counts, so the two surfaces cannot disagree about a project. */
  progress: ProjectProgress;
}

/**
 * Whose work an overview is about — the same three the list takes.
 *
 * `project` carries the id; the other two do not, which is why this is a union rather than two
 * loose optional fields a caller could combine into "a project scope with no project".
 */
export type OverviewScope =
  { kind: 'mine' } | { kind: 'all' } | { kind: 'project'; projectId: string };

/**
 * The query key for an overview, so the three screens that read one cannot disagree about it.
 *
 * The counts are scope-dependent now, so a single key would have served the Tasks screen under
 * *All* out of the cache Today's bands filled under `mine` — the exact disagreement the scope
 * parameter was added to end, reintroduced one layer up in react-query. Still under `['tasks']`,
 * so every mutation that invalidates that prefix still invalidates all of them.
 */
export const taskOverviewKey = (scope: OverviewScope = { kind: 'mine' }) =>
  [
    'tasks',
    'overview',
    scope.kind === 'project' ? `project:${scope.projectId}` : scope.kind,
  ] as const;

export const getTaskOverview = (scope: OverviewScope = { kind: 'mine' }) => {
  const q = new URLSearchParams({ scope: scope.kind });
  if (scope.kind === 'project') q.set('project_id', scope.projectId);
  return apiFetch<TaskOverview>(`/api/tasks/overview?${q.toString()}`);
};

/** One browser signed in to this account. */
export interface SessionSummary {
  id: string;
  userAgent: string | null;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  /** True for the session making the request — so the list can say "this device". */
  current: boolean;
}

export const listSessions = () => apiFetch<SessionSummary[]>('/api/auth/sessions');

export const revokeSession = (id: string) =>
  apiFetch<void>(`/api/auth/sessions/${id}`, { method: 'DELETE' });

export const revokeOtherSessions = () =>
  apiFetch<{ revoked: number }>('/api/auth/sessions/revoke-others', { method: 'POST' });

/** Work promised to an earlier day and never done. */
export interface CarriedOverTask {
  id: string;
  title: string;
  scheduledDate: string;
  /** Days between the promise and the day being viewed. */
  daysAgo: number;
  /** The deadline, which is a different clock from the promise — see `CarriedOver`. */
  dueAt: string | null;
  status: string;
  priority: number;
  /** The project it sits in, or null — what the band reads the viewer's role against. */
  projectId: string | null;
  /**
   * The goal that has been stood down, when every goal this task serves has been.
   *
   * The shortlist below drops this work entirely — see `getDayPlan`. This band keeps the row and
   * says why, because the "Today" it used to offer dated the task to a day that would then not
   * show it anywhere: not in the shortlist, not in Goals today, and no longer here either.
   */
  parkedGoal: { id: string; title: string; status: GoalStatus } | null;
}

export interface CarriedOver {
  tasks: CarriedOverTask[];
  /** Tasks dated to the previous day, done or not — tells "finished it" from "never planned". */
  previousDayPlanned: number;
}

export const listCarriedOver = (date: string) =>
  apiFetch<CarriedOver>(`/api/day/${date}/carry-over`);

export const carryOver = (date: string, body: { taskIds?: string[]; unschedule?: boolean } = {}) =>
  apiFetch<{ moved: number }>(`/api/day/${date}/carry-over`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
